import React, { useRef, useState, useEffect } from "react";
import { DataFeed } from "./datafeed";
import { makeStyles } from "@material-ui/core/styles";
import { fetchKLine, fetchSymbols, IApiKLine, IApiSymbol } from "./services";
import stateQuery from "./datafeed/useStateQuery";
import { useSearchParams } from "react-router-dom";
import useKline from "./datafeed/useKline";
import { ChartingLibraryWidgetOptions } from "./../public/charting_library/charting_library.d";

declare global {
  interface Window {
    TradingView: {
      widget: TradingView.IChartWidgetApi;
    };
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  menu: {
    marginRight: theme.spacing(2),
  },
}));

const Resolution = {
  "1": { server: "1min", name: "1m", websocket: 60 },
  "5": { server: "5min", name: "5m", websocket: 300 },
  "30": { server: "30min", name: "30m", websocket: 1800 },
  "60": { server: "1hour", name: "1h", websocket: 3600 },
  "240": { server: "4hour", name: "4h", websocket: 14400 },
  "1440": { server: "1day", name: "1D", websocket: 86400 },
  "4320": { server: "3day", name: "3D", websocket: 259200 },
  "10080": { server: "1week", name: "1W", websocket: 604800 },
};

const icon_url = "https://static.okex.com/cdn/oksupport/asset/currency/icon/";

function App() {
  const [cryptoPair, setCryptoPair] = useState<String | null>("btcusdt");
  const [chartType, setChartType] = useState<String | null>("usdt");
  const currencyPairInput = useRef(null);
  // const currencyPairChange = (pair: any) => {
  //   setCryptoPair(pair.toLowerCase());
  // };
  const [searchParams] = useSearchParams();

  // if (cryptoPair === null) {
  //   setCryptoPair("btcusdt");
  // }
  const [visible, setVisible] = useState(false);
  const interval = useRef<keyof typeof Resolution>("1440");
  const container = useRef<HTMLDivElement | null>(null);
  const cryptoPairRef = useRef<String | null>(null);
  const widget = useRef<TradingView.IChartingLibraryWidget>();
  const datafeed = useRef<DataFeed>();
  const [info, setInfo] = useState<IApiSymbol>();
  const infoRef = useRef(info);
  const ws = useRef<WebSocket>();
  const [currentTime, setCurrentTime] = useState(0);
  const [lastTime, setLastTime] = useState(0);
  const [tetherPrice, setTetherPrice] = useState(0);

  // اطلاعات اخرین کندل ها اینجاست
  let liveCandle: IApiKLine = stateQuery(
    cryptoPairRef.current,
    Resolution[interval.current]
  ) as never;

  const [initialized, setInitialized] = useState(false);

  const historyPage = useRef(2);

  const [historyDetails, setHistoryDetails] = useState({
    page: historyPage.current,
    symbol: infoRef.current?.symbol! || cryptoPair,
  });

  // از پروژه بیتازر پیام به ifram فرستاده میشه
  // اینجا دریافت میشه
  window.onmessage = (e) => {
    const message = e.data;
    if (typeof message === "object" && message !== null && message.pair !== undefined) {
      setCryptoPair(message.pair);
      setChartType(message.type);
      setTetherPrice(message.tetherPrice);
    } else {
      setCryptoPair("btcusdt");
      setChartType("usdt");
    }
  };

  useEffect(() => {
    cryptoPairRef.current = cryptoPair;
  }, [cryptoPair]);

  useEffect(() => {
    infoRef.current = info;
  });

  // اگه live candle
  // دیتا نداشت از فتچ استفاده میکنه
  useEffect(() => {
    if (ws.current) {
      return;
    }

    const init = async () => {
      let res = (await fetchSymbols()) as any;

      if (!res) return;

      const cryptoNames: string[] = [];

      cryptoNames.push(...Object.keys(res));

      const arr = cryptoNames.sort();
      const newList = arr.map((k) => res[k]);
      const newInfo = newList.find((e) => {
        return e.name === cryptoPair?.toUpperCase()!;
      });

      setInfo(newInfo || newList[0]);
      initDatafeed(newInfo || newList[0]);
      initTradingView(newInfo || newList[0]);
    };

    //eslint-disable-next-line
    if (liveCandle.time !== null && liveCandle.time !== 0 && initialized === false) {
      init();
      setInitialized(true);
    }
  }, [liveCandle]);

  useEffect(() => {
    const init = async () => {
      // این ریسپانس یک ابجکت که اطلاعات ارزهای مختلف شامل میشه به ما برمیگردونه
      let res = (await fetchSymbols()) as any;

      if (!res) return;

      // کلید های ابجک جدا میشن
      const cryptoNames: string[] = [];
      cryptoNames.push(...Object.keys(res));

      const arr = cryptoNames.sort();
      const newList = arr.map((k) => res[k]);

      const newInfo = newList.find((e) => {
        return e.name === cryptoPair?.toUpperCase()!;
      });

      setInfo(newInfo || newList[0]);
      initDatafeed(newInfo || newList[0]);
      initTradingView(newInfo || newList[0]);
    };
    init();
  }, [cryptoPair]);

  const initDatafeed = (data?: IApiSymbol) => {
    // اطلاعات ارزفعال
    const currentInfo = data || info;

    if (!currentInfo) return;

    // اسم ارزر فعال که بالای صفحه است اینجا ساخته میشه
    const display_name = `${currentInfo["trading_name"]}${chartType?.toUpperCase()}`;

    // تایم فریم های فعال مانند 24 ستعته یا 5 دقیقه ای و ..
    const supported_resolutions = Object.keys(Resolution) as never;

    // اینجا datafeed
    // مقدار دهی میشه و در پایین تر جاهای مختلف استفاده میشه
    // در واقع شامل یک ابجکت که اطلاعات کلی چارت و کندل ها رو درون خودش داره
    datafeed.current = new DataFeed({
      SymbolInfo: {
        name: display_name,
        full_name: display_name,
        description: "",
        type: "stock",
        session: "24x7",
        exchange: "",
        listed_exchange: "",
        timezone: "Asia/Tehran",
        format: "price",
        pricescale: Math.pow(10, +currentInfo["pricing_decimal"]),
        minmov: 1,
        volume_precision: +currentInfo["trading_decimal"],
        has_intraday: true,
        supported_resolutions: supported_resolutions,
        has_weekly_and_monthly: true,
        has_daily: true,
      },
      DatafeedConfiguration: {
        supported_resolutions: supported_resolutions,
      },
      getBars: useGetBars,
    });
  };

  // اینجا اخرین کندل رو لایو نگهداری میکنه و باعث تغییرات لحظه ای در اخرین کندل میشه
  useEffect(() => {
    if (chartType === "usdt") {
      datafeed.current?.updateBar({
        // به liveCandle در بالاتر اشاره شد
        time: liveCandle.time * 1000,
        open: liveCandle.open,
        high: liveCandle.high,
        low: liveCandle.low,
        close: liveCandle.close,
        volume: liveCandle.volume,
      });
    }
    if (chartType === "irt") {
      datafeed.current?.updateBar({
        // به liveCandle در بالاتر اشاره شد
        time: liveCandle.time * 1000,
        open: liveCandle.open * tetherPrice,
        high: liveCandle.high * tetherPrice,
        low: liveCandle.low * tetherPrice,
        close: liveCandle.close * tetherPrice,
        volume: liveCandle.volume,
      });
    }
    const date = new Date();
    const now = date.getTime();

    setLastTime(Math.floor(now / 1000 / 300));

    if (liveCandle.time !== currentTime) {
      setCurrentTime(liveCandle.time);
    }
  }, [liveCandle, cryptoPair, chartType]);

  const historyDataResult = useRef([]);
  //هوک پایین دیتای شامل ارایه و اجکت هایی رو برمیگردونه که هر ابجکت اطلاعات یک کندل
  // در واقع تاریخچه قیمتی رو برمیگردونه که هر آبجکت یک کندل می باشد
  historyDataResult.current = useKline(
    cryptoPair || "",
    Resolution[interval.current],
    historyDetails.page
  ) as any;

  useEffect(() => {
    setHistoryDetails((prev) => ({
      ...prev,
      page: historyPage.current,
      symbol: infoRef.current?.symbol! || cryptoPair!,
    }));
  }, [historyPage.current, infoRef.current?.symbol, cryptoPair]);

  const useGetBars = async (
    symbolInfo: TradingView.LibrarySymbolInfo,
    resolution: TradingView.ResolutionString,
    periodParams: TradingView.PeriodParams,
    onResult: TradingView.HistoryCallback,
    onError: TradingView.ErrorCallback
  ) => {
    const bars: TradingView.Bar[] = [];
    const size = window.innerWidth;

    if (!periodParams.firstDataRequest) {
      const res = historyDataResult.current as any;

      if (!res || !res.length) {
        onResult(bars, { noData: true });
        return;
      }

      historyPage.current = historyPage.current + 1;

      for (let i = 0; i < res.length; i++) {
        const item = res[i];
        bars.push({
          time: item.id * 1000,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          volume: item.vol,
        });
      }

      bars.sort((l, r) => (l.time > r.time ? 1 : -1));

      if (periodParams.firstDataRequest) {
        subscribeKLine();
      }

      onResult(bars);

      return;
    }

    if (resolution !== interval.current) {
      unsubscribeKLine();
      interval.current = resolution as keyof typeof Resolution;
    }

    const res = await fetchKLine(
      infoRef.current?.name!,
      Resolution[interval.current].server,
      size > 500 ? 500 : size
    );

    if (!res || !res.length) {
      onResult(bars, { noData: true });
      return;
    }

    if (periodParams.countBack > 400) {
      return;
    }

    // اینجا جایی هستش که درنهایت اطلاعات
    // در صفحه قرار میگه البته توجه بشه که اطلاعات تاریخچه ساخته میشود و شامل اخرین کندل نمی شود

    if (chartType === "usdt") {
      for (let i = 0; i < res.length; i++) {
        const item = res[i];
        bars.push({
          time: item.id * 1000,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          volume: item.vol,
        });
      }
    }

    if (chartType === "irt") {
      for (let i = 0; i < res.length; i++) {
        const item = res[i];
        bars.push({
          time: item.id * 1000,
          open: item.open * tetherPrice,
          high: item.high * tetherPrice,
          low: item.low * tetherPrice,
          close: item.close * tetherPrice,
          volume: item.vol,
        });
      }
    }
    bars.sort((l, r) => (l.time > r.time ? 1 : -1));

    if (periodParams.firstDataRequest) {
      subscribeKLine();
    }

    onResult(bars);
  };

  const initTradingView = (data?: IApiSymbol) => {
    const currentInfo = data || info;
    widget.current = new window.TradingView.widget({
      locale: "en",
      fullscreen: true,
      theme: "Light",
      timezone: "Asia/Tehran",
      symbol: currentInfo?.name,
      interval: interval.current as never,
      container: container.current!,
      datafeed: datafeed.current!,
      //library_path: "https://chart.bitazar.com/charting_library/",
      library_path: "./charting_library/",
    });
  };

  const onClick = () => {
    setVisible(!visible);
  };

  const onSelected = (e: IApiSymbol) => {
    if (!info || !widget.current) {
      return;
    }
    if (e.symbol !== info.symbol) {
      setInfo(e);
      unsubscribeKLine();
      interval.current = "5";
      widget.current.setSymbol(`${info["name"]}`, "5" as never, () => {});
    }
  };

  const subscribeKLine = () => {
    const sub = `market.${infoRef.current?.symbol}.kline.${
      Resolution[interval.current].server
    }`;
    ws.current?.send(JSON.stringify({ id: "tv", sub: sub }));
  };

  const unsubscribeKLine = () => {
    const sub = `market.${infoRef.current?.symbol}.kline.${
      Resolution[interval.current].server
    }`;
    ws.current?.send(JSON.stringify({ id: "tv", sub: sub, cmd: "unsub" }));
  };

  const styles = useStyles();
  const title = info ? `${info["name"]}` : "";

  return (
    <div className="app">
      <div className={styles.root}></div>
      <div className="container" ref={container} />
    </div>
  );
}

export default React.memo(App);
